import i18n from 'src/utils/translate';
import { useHelpScout } from 'src/utils/hooks';
import { fetchNHSTagContent } from 'src/pages/tag/actions';
import { setAlert } from 'src/typescript/components/alert/actions';
import { lockPost } from 'src/modules/community/singlePost/actions/actions';
import actionTypes from './actionTypes';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event } from 'src/constants/footfall';
import { removeRecipientFromUrl } from 'src/utils/router';
import ModalType from 'src/constants/modalTypes';
import Router from 'next/router';
import { Community, State } from 'src/types';
import { signup, signupWithSocial } from '../auth/actions';
import { SignupFlow } from './components/signup/signupSurvey';
import { LinkButton } from 'styles-js/buttons';

type ModalProps = {
  [key: string]: unknown;
}

export function showModal({
  modalType = ModalType.DEFAULT,
  modalProps = {},
}: {modalType?: ModalType, modalProps?: ModalProps}) {
  return { type: actionTypes.MODAL_SET, modalType, modalProps };
}

export function setModalProps(modalProps: ModalProps) {
  return { type: actionTypes.MODAL_SET_PROPS, modalProps };
}

export function closeModal() {
  return { type: actionTypes.MODAL_UNSET };
}

type ShowSignupModal = {
  group?: Community;
  noLoginClose?: boolean;
  defaultEmail?: string;
  onSignupSuccess?: () => void;
  redirectPage?: () => void;
  signupFlow?: SignupFlow;
}
export function showSignupModal({
  group,
  noLoginClose,
  defaultEmail = '',
  onSignupSuccess = () => {},
  redirectPage,
  signupFlow,
}: ShowSignupModal = {}) {
  return showModal({
    modalType: ModalType.SIGN_UP,
    modalProps: {
      group,
      noLoginClose,
      defaultEmail,
      onSignupSuccess,
      redirectPage,
      signupFlow,
    },
  });
}

export type SuggestedCommunity = {
  name: string;
  title: string;
  slug: string;
  followerCount: number;
  logoImageHash: string;
  imageUrl: string;
  url: string;
}

export function showHuLocalSignupModal(suggestedCommunities: SuggestedCommunity[]) {
  return showModal({
    modalType: ModalType.HULOCAL_SIGN_UP,
    modalProps: {
      suggestedCommunities,
    },
  });
}

type ShowLoginModal = {
  noClose?: boolean;
  onLoginSuccess?: () => void;
  username?: string;
  clickMetaData?: {[key:string]: string};
}

export function showLoginModal({
  noClose = false,
  onLoginSuccess = () => {},
  username = '',
  clickMetaData,
}: ShowLoginModal = {}) {
  trackFootfallEvent(Event.CLICKED, { clickType: 'login-modal-viewed', clickMetaData });

  return showModal({
    modalType: ModalType.LOGIN,
    modalProps: {
      noClose,
      onLoginSuccess,
      username,
    },
  });
}

type Authdata = {
  email: string;
  password: string;
  birthday?: string;
  dateOfBirth?: string;
  googleUserId?: string;
}
type SurveyProps = {
  groupCode?: string;
  suggestedCommunities?: any[];
  redirectPage?: () => void;
}
export function showSignupSurvey(
  {email, password}: Authdata,
  onSignupSuccess: () => void,
  {suggestedCommunities = [], groupCode, redirectPage}: SurveyProps,
) {
  return async (dispatch: any, getState: () => State) => {
    const signupFlow = getState().modal.modalProps?.signupFlow || !!groupCode && SignupFlow.Community;
    await dispatch(closeModal());
    return dispatch(showModal({
      modalType: ModalType.SIGN_UP_SURVEY,
      modalProps: {
        suggestedCommunities,
        signupFlow,
        redirectPage,
        signupUser: async ({ username }: {username: string}) => {
          await dispatch(signup({ password, username, email, isCommunitySignup: !!groupCode }));
          if (onSignupSuccess) onSignupSuccess();
        },
      },
    }));
  };
}

export function showSignupSurveySocial(
  authdata: Authdata,
  onSignupSuccess: () => void,
  {suggestedCommunities = [], groupCode, redirectPage}: SurveyProps,
) {
  return async (dispatch: any, getState: () => State) => {
    // Explicit facebook DOB
    if (authdata.birthday) {
      const birthday = authdata.birthday.split('/');
      authdata.dateOfBirth = birthday[2] + '/' + birthday[0] + '/' + birthday[1];
    }
    const signupFlow = getState().modal.modalProps.signupFlow || !!groupCode && SignupFlow.Community;
    await dispatch(closeModal());
    return dispatch(showModal({
      modalType: ModalType.SIGN_UP_SURVEY,
      modalProps: {
        suggestedCommunities,
        signupFlow,
        redirectPage,
        signupUser: async ({ username }: {username: string}) => {
          await dispatch(signupWithSocial({...authdata, username}, authdata.googleUserId ? 'Google' : 'Facebook', !!groupCode));
          if (onSignupSuccess) onSignupSuccess();
        },
      },
    }));
  };
}

export function showExistingLoginSocialModal(authdata, onLoginSuccess) {
  return showModal({
    modalType: ModalType.EXISTING_LOGIN_SOCIAL,
    modalProps: {
      authdata,
      onLoginSuccess,
    },
  });
}

export function showDFPModal(content: unknown) {
  return (dispatch: any, getState: () => State) => {
    // BB-1274: Prevent Google Ad Manager (replaces DFP) popups from overwriting our Sign up Modal
    const { modal: { modalType }} = getState();
    if (modalType) return;

    return dispatch(showModal({
      modalType: ModalType.DFP,
      modalProps: { content },
    }));
  };
}

export function showConfirmModal(modalProps: ModalProps) {
  return showModal({ modalType: ModalType.CONFIRM, modalProps });
}

export function showTakeProfileSurveyModal() {
  trackFootfallEvent(Event.PROFILE_SURVEY, { clickType: 'survey-param' });
  return showModal({ modalType: ModalType.TAKE_PROFILE_SURVEY });
}

export function showTurnOffRepliesModal(postId, communitySlug, onTurnOff) {
  return (dispatch: any) => {
    return dispatch(showModal({
      modalProps: {
        title: i18n.t('Are you sure you want to do this?'),
        cancel: i18n.t('Cancel'),
        accept: i18n.t('Turn off replying'),
        onAccept: async () => {
          await dispatch(lockPost(postId, communitySlug));
          onTurnOff();
          dispatch(setAlert(i18n.t('You turned off replying for this post.'), 'success'));
        },
        children: (
          <p>
            {i18n.t('This will prevent users from creating or editing replies and the author of the post will be notified that replying has been disabled.')}<br />
            <LinkButton onClick={() => useHelpScout('36-turn-off-replying-on-posts')}>{i18n.t('Learn more')}</LinkButton>
          </p>
        )},
    }));
  };
}

const HIDE_MODAL_CLOSE_BUTTON = '0';
const OPEN_SIGNUP_NORMALLY = '1';
const INACTIVE_USER_SURVEY = '2';
const SURVEY = '3';
const COVID_19_FEATURED_POST_MODAL = '19';
const PROFILE_SURVEY = 'profile';

export const handleQueryParams = (queryParams, footfallSection = '') => {
  return async (dispatch: any, getState: () => State) => {
    if (!queryParams) return;
    // To fix: URLs with hash don't have the params below so opening the welcome back modal doesn't work
    const { utm_source, popup, exit, collector, recipient, survey } = queryParams;
    const recipientParam = recipient;
    const { context: { currentUser, currentCommunity } } = getState();
    // exception, other modals take priority, we prevent double footfall events by checking the route
    const isEditProfilePage = Router.route === '/user/editProfile';

    if (popup === COVID_19_FEATURED_POST_MODAL) {
      await dispatch(fetchNHSTagContent('coronavirus-covid-19', 'conditions'));
      dispatch(showModal({
        modalType: ModalType.NHS_TAG_CONTENT,
        modalProps: {
          tagName: 'Coronavirus (COVID-19)',
        },
      }));
      return;
    }

    if (popup === INACTIVE_USER_SURVEY) {
      dispatch(showModal({
        modalType: ModalType.IFRAME,
        modalProps: {
          iframe: {
            src: `https://www.smartsurvey.co.uk/s/O6STRR/?id=${recipient}`,
            height: '605px',
          },
        },
      }));
      return;
    }

    if (popup === SURVEY) {
      dispatch(showModal({
        modalType: ModalType.IFRAME,
        modalProps: {
          iframe: {
            src: `https://surveys.healthunlocked.com/s/${collector}/?id=${recipient}`,
            height: '605px',
          },
        },
      }));
      return;
    }

    if (popup in ModalType) {
      dispatch(showModal({modalType: popup}));
    }

    if (currentUser) {
      if (recipientParam) removeRecipientFromUrl();

      // popup=profile used in featured posts, survey=true in emails
      if ((survey === 'true' || popup === PROFILE_SURVEY) && !isEditProfilePage) {
        dispatch(showTakeProfileSurveyModal());
        return;
      }

      return;
    }

    // Only for visitors
    if (utm_source === 'notification' || utm_source === 'iosapp') {
      dispatch(showLoginModal({ noClose: true, username: recipientParam }));
      if (recipientParam) {
        // delay removing param to make sure we display username in login modal
        setTimeout(() => removeRecipientFromUrl(), 1500);
      }

    } else if (popup === OPEN_SIGNUP_NORMALLY) {
      const hideClose = exit === HIDE_MODAL_CLOSE_BUTTON;
      trackFootfallEvent(Event.CLICKED, { clickType: 'sign-up', clickMetadata: { section: footfallSection}});
      dispatch(showSignupModal({group: currentCommunity, noLoginClose: hideClose}));
    }
  };
};
